import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './context/ToastContext';

// Layout & Security
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';

// Pages
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';

// Master Pages
import AccountList from './pages/Accounts/AccountList';
import ProductList from './pages/Products/ProductList';

// Transactions
import SaleEntry from './pages/Sales/SaleEntry';
import SaleList from './pages/Sales/SaleList';
import PurchaseEntry from './pages/Purchases/PurchaseEntry';
import PurchaseList from './pages/Purchases/PurchaseList';

// Payments & Operations
import AccountPayment from './pages/Payments/AccountPayment';
import BoxTracking from './pages/BoxEntry/BoxTracking';
import OtherExpenses from './pages/Expenses/OtherExpenses';

// Reports
import UnifiedStatement from './pages/Reports/UnifiedStatement';
import SaleReport from './pages/Reports/SaleReport';
import PurchaseReport from './pages/Reports/PurchaseReport';
import StockReport from './pages/Reports/StockReport';
import OutstandingReport from './pages/Reports/OutstandingReport';

// Settings
import WhatsAppSettings from './pages/Settings/WhatsAppSettings';
import FirmSettings from './pages/Settings/FirmSettings';
import ChangePassword from './pages/Settings/ChangePassword';

// Layout wrapper for authenticated dashboard view with Mobile Sidebar support
const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar onToggleSidebar={() => setSidebarOpen((prev) => !prev)} isSidebarOpen={sidebarOpen} />
      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        {/* Mobile backdrop overlay */}
        {sidebarOpen && (
          <div
            className="sidebar-backdrop show d-lg-none"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <main className="main-content flex-grow-1 bg-light">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <ToastProvider>
      <Router>
        <Routes>
        {/* Public Login Route */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected Dashboard & App Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Master Management */}
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProductList />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AccountList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Sales */}
        <Route
          path="/sales"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SaleList />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SaleEntry />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/edit/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SaleEntry />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Purchases */}
        <Route
          path="/purchases"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PurchaseList />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PurchaseEntry />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases/edit/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PurchaseEntry />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Payments & Operations */}
        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AccountPayment />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments/in"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AccountPayment />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments/out"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AccountPayment />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/box-tracking"
          element={
            <ProtectedRoute>
              <AppLayout>
                <BoxTracking />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OtherExpenses />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="/reports/statement"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UnifiedStatement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/sales"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SaleReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/purchases"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PurchaseReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/stock"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StockReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/outstanding"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OutstandingReport />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="/settings/whatsapp"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WhatsAppSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/firm"
          element={
            <ProtectedRoute>
              <AppLayout>
                <FirmSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/password"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChangePassword />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Root Redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  </ToastProvider>
  );
}

export default App;
